<template>
  <v-form ref="form">
    <FormPanels>
      <FormPanel label="Entity name" v-if="!linkMode">
        <FormRow
          required
          label="Entity name"
          tooltip="This is the title for your entity."
        >
          <TextField
            :rules="[validation.required]"
            placeholder="Name"
            v-model="form.title"
            wide
          ></TextField>
        </FormRow>

        <template v-if="form.schema && form.schema.use_random_tech_names">
          <!-- Hidden input for create mode to ensure tech_name is submitted -->
          <input
            v-if="!form.id"
            type="hidden"
            name="tech_name"
            :value="form.tech_name"
          />
          <!-- don't ask me why I had to do this because I don't know -->
          <div
            aria-hidden="true"
            style="
              width: 1px;
              height: 1px;
              position: absolute;
              top: 0;
              left: 0;
              overflow: hidden;
            "
          >
            {{ form.tech_name }}
          </div>
        </template>

        <template v-else>
          <FormRow
            required
            label="Tech name"
            tooltip="This is the tech name for your entity."
          >
            <TextField
              :rules="[validation.required]"
              placeholder="Tech Name"
              v-model="form.tech_name"
            ></TextField>
          </FormRow>
        </template>

        <!-- <FormRow
          required
          label="URL"
          tooltip="This is the tech name for your entity."
          v-if="form.schema.schema_type === 'page'"
        >
          <TextField
            :rules="[validation.required]"
            placeholder="URL Path"
            v-model="form.page_attributes.url"
          >
            <template v-slot:prepend>
              <template v-if="form.schema.base_url">
                {{ `/${form.schema.base_url}/` }}
              </template>
              <template v-else> / </template>
            </template>
          </TextField>
        </FormRow> -->

        <div
          v-if="
            form.schema && form.schema.page_ids && form.schema.page_ids.length
          "
        >
          <FormRow
            label="Entity Meta Tags"
            tooltip="Add any meta data tags you'd like in this field."
          >
            <MetaTagsEditor v-model="form.meta_tags" />
          </FormRow>
        </div>
      </FormPanel>
      <template v-if="form.schema && form.schema.schema_type === 'page'">
        <FormPanel label="Page Info">
          <FormRow label="Url Type" required>
            <template #tooltip>
              Select whether you want this page to live within your Commons
              domain (Branch URL) or link "offsite" (External Link).
            </template>
            <RadioGroup
              :items="urlTypes"
              v-model="form.page_attributes.url_type"
            />
          </FormRow>
          <FormRow
            v-if="
              ![
                'none',
                'anchor_on_page',
                'open_submission_form',
                'call_custom_component',
              ].includes(form.page_attributes.url_type)
            "
            tooltip="Here you can give your page a custom URL at your Commons domain."
            label="Custom URL"
            :required="
              ['absolute', 'redirect'].includes(form.page_attributes.url_type)
            "
          >
            <v-layout
              align-center
              v-if="
                form.page_attributes.url_type === 'relative' ||
                form.page_attributes.url_type === 'redirect'
              "
            >
              <span class="mr-3">
                <div>
                  <small>{{ getCustomUrlPrefix[0] }}</small>
                </div>
                <div>
                  <small>{{ getCustomUrlPrefix[1] }}</small>
                </div>
              </span>
              <div class="flex-grow-1">
                <TextField
                  placeholder="Custom URL"
                  :value="urlWithoutPrefix"
                  @input="updateUrl"
                  wide
                  :rules="[rules.customUrl]"
                />
              </div>
            </v-layout>

            <TextField
              v-else
              placeholder="https://example.com"
              v-model="form.absolute_url"
              :rules="[validation.required, validation.urlOrMailto]"
            />

            <div v-if="pageUrlHasUnderscore" class="warning-message mt-1">
              ⚠️ Underscores in URLs are less readable than hyphens.
            </div>
          </FormRow>
          <FormRow
            v-if="['redirect'].includes(form.page_attributes.url_type)"
            tooltip="Here you can give your page a custom anchor for a section on your single-page Commons domain. (Components in this page will be ignored.)"
            label="Redirect URL"
            required
          >
            <TextField
              placeholder="Redirect"
              v-model="form.page_attributes.absolute_url"
            />
          </FormRow>

          <input
            type="hidden"
            name="page_attributes[title]"
            :value="form.page_attributes.title"
          />

          <div v-if="form.page_attributes.url_type == 'relative'">
            <FormRow
              tooltip="This is the title of your page, as it appears in browsers or tabs. Leave blank to use the Site Headline from Site Settings."
              label="Page Display Title (in browser/tab)"
            >
              <TextField
                :placeholder="
                  siteDefaults.headline || 'Page Display Title (in browser/tab)'
                "
                v-model="form.page_attributes.headline"
              ></TextField>
              <InheritedTextHint
                :value="form.page_attributes.headline"
                :default-value="siteDefaults.headline"
                field-label="page title"
                @reset="form.page_attributes.headline = ''"
              />
            </FormRow>

            <FormRow
              label="Page Description"
              tooltip="Add a description to help define this page's purpose. Leave blank to use the Site Description from Site Settings."
            >
              <TextArea
                :placeholder="siteDefaults.description || 'Description'"
                v-model="form.page_attributes.description"
              ></TextArea>
              <InheritedTextHint
                :value="form.page_attributes.description"
                :default-value="siteDefaults.description"
                field-label="page description"
                @reset="form.page_attributes.description = ''"
              />
            </FormRow>

            <FormRow
              label="Social Share Image"
              tooltip="Image that is shown in social shares (16:9 aspect ratio). Leave empty to use the default social share image from Site Meta Tags."
            >
              <Uploader
                v-model="form.page_attributes.social_share_image"
                :ratio="1200 / 630"
                enableCropper
              />
              <InheritedImageHint
                :value="form.page_attributes.social_share_image"
                :default-image-url="siteDefaultSocialShareImageUrl"
                @reset="form.page_attributes.social_share_image = null"
              />
            </FormRow>
          </div>
        </FormPanel>
        <FormPanel label="Page Details" closedByDefault>
          <div v-if="form.page_attributes.url_type == 'relative'">
            <FormRow
              label="Entity Meta Tags"
              tooltip="Add any metadata tags you'd like in this field."
            >
              <MetaTagsEditor v-model="form.page_attributes.meta_tags" />
            </FormRow>
            <FormRow
              label="Venue Links Here"
              tooltip="If links for a venue in event lists should go to this page, select the venue here."
              v-if="
                currentUser.active_project.available_addon_names.includes(
                  'Addon::Ticketmaster'
                )
              "
            >
              <TicketmasterVenueSelector
                v-model="form.page_attributes.ticketmaster_venue_id"
                enabled
              />
            </FormRow>
            <FormRow
              label="Page Theme (Default If Empty)"
              tooltip="Choose the theme for this page."
            >
              <ThemesSelector
                v-model="form.page_attributes.theme_id"
              ></ThemesSelector>
            </FormRow>
            <FormRow
              label="Role-based Access"
              tooltip="Select a role or roles to restirct access to this page for those groups of users."
            >
              <RolesSelector
                multiple
                v-model="form.page_attributes.role_ids"
              ></RolesSelector>
            </FormRow>
            <FormRow
              label="Require Authentication"
              tooltip="Select this checkbox if you want to require site authentication to access this page."
            >
              <Checkbox v-model="form.page_attributes.authorized" />
            </FormRow>
          </div>

          <FormRow
            required
            label="Add to Header"
            tooltip="Select if you want this page linked in the header or hidden."
          >
            <RadioGroup
              :items="addToMenuItems"
              v-model="form.page_attributes.add_to_menu"
            />
          </FormRow>

          <div v-if="['menu_bar'].includes(form.page_attributes.add_to_menu)">
            <FormRow label="Add to Sub-Menu" tooltip="Select a parent page.">
              <PagesSelector
                :condition="(page) => page.id != form.page_attributes.id"
                v-model="form.page_attributes.parent_menu_id"
              />
            </FormRow>
          </div>
          <div
            v-if="
              ['menu_bar', 'grid_menu'].includes(
                form.page_attributes.add_to_menu
              )
            "
          >
            <FormRow
              required
              label="Page Name in Header"
              tooltip="This is the name of your page, as it appears in the header and menu navigation."
            >
              <TextField
                :rules="[validation.required]"
                placeholder="Page Name in Header"
                v-model="form.page_attributes.menu_title"
              />
            </FormRow>
            <FormRow v-if="form.page_attributes.add_to_menu == 'grid_menu'">
              <template #label>Thumbnail Image<br />(320x180)</template>
              <Uploader
                :ratio="320 / 180"
                v-model="form.page_attributes.grid_menu_image"
                enableCropper
              />
            </FormRow>
            <FormRow
              label="Header Position"
              tooltip="This is the order in which pages are listed in the header menu."
            >
              <TextField
                type="number"
                v-model="form.page_attributes.menu_position"
                placeholder="Header Position"
              />
            </FormRow>
            <FormRow
              label="Menu Link Type"
              tooltip="Select whether you want the link to appear as text or a button."
            >
              <RadioGroup
                :items="menuStyles"
                v-model="form.page_attributes.menu_style"
              />
            </FormRow>
            <div v-if="form.page_attributes.menu_style == 'button'">
              <FormRow label="Button Background">
                <ColorPicker
                  v-model="form.page_attributes.menu_button_background"
                />
              </FormRow>
              <FormRow label="Button Text Color">
                <ColorPicker
                  v-model="form.page_attributes.menu_button_text_color"
                />
              </FormRow>
            </div>
          </div>

          <FormRow
            label="Add to Footer"
            tooltip="Check the box if you want this page to be linked in the footer as well."
          >
            <Checkbox v-model="form.page_attributes.add_to_footer"></Checkbox>
          </FormRow>

          <div v-if="form.page_attributes.add_to_footer">
            <FormRow required label="Page Title in the Footer">
              <TextField
                :rules="[validation.required]"
                placeholder="Page Title in the Footer"
                v-model="form.page_attributes.footer_menu_title"
              />
            </FormRow>

            <FormRow label="Position in the Footer">
              <TextField
                type="number"
                placeholder="Position in the Footer"
                v-model="form.page_attributes.footer_position"
              ></TextField>
            </FormRow>
          </div>

          <FormRow
            label="Enable Lenis"
            tooltip="Check the box if you want to enable Lenis for this page."
          >
            <Checkbox v-model="form.page_attributes.enable_lenis"></Checkbox>
          </FormRow>

          <FormRow
            v-if="form.page_attributes.url_type == 'parent_menu'"
            label="Parent Menu link to page"
            tooltip="Select a page for this parent menu item to link to."
          >
            <PagesSelector
              :condition="(page) => page.id != form.page_attributes.id"
              v-model="form.page_attributes.parent_menu_link_to_id"
            />
          </FormRow>
        </FormPanel>
        <FormPanel label="Custom Styles">
          <small>
            <b>{{ form.page_attributes.title || "page" }}.css</b>
          </small>
          <CodeEditor v-model="form.page_attributes.css" />
        </FormPanel>
      </template>
      <FormPanel label="Fields">
        <SchemaBuilder
          v-if="form.schema_id"
          :schemaId="form.schema_id"
          v-model="form.fields"
          @setName="submitText = $event"
          @uploading-status-changed="updateUploadingStatus"
        />
        <template #actions v-if="form.id">
          <v-btn icon @click="copyLink">
            <v-icon size="20">$copy_link</v-icon>
          </v-btn>
        </template>
      </FormPanel>
    </FormPanels>
    <SubmitFooter
      :refs="$refs"
      :submit="submit"
      :disabled="isSchemaFieldUploading"
    >
      {{ form.id ? "Update" : "Add" }} {{ submitText }}
    </SubmitFooter>
  </v-form>
</template>

<script>
import mixin from "../../_extends/form.extends.js";
import siteDefaultsMixin from "../../_extends/site-defaults.mixin.js";
import { copyToClipboard } from "@/lib";
export default {
  extends: mixin,
  mixins: [siteDefaultsMixin],
  data() {
    return {
      submitText: this.btnText,
      linkMode: false,
      roles: [],
      addToMenuItems: [
        {
          value: "menu_bar",
          title: "Navigation Menu",
          tooltip:
            "The Menu Bar is located at the top-right of your Page and contains links.",
        },
        {
          value: "grid_menu",
          title: "Grid Menu",
          tooltip:
            "The Grid Menu is a grid of content blocks that link to different Pages. ",
        },
        {
          value: "hidden",
          title: "Hide Page",
          tooltip:
            "This setting allows you to hide a Page. It doesn’t delete your Page, but you can only access it by its unique URL.",
        },
      ],
      urlTypes: [
        { value: "relative", title: "Branch URL" },
        // { value: "absolute", title: "External Link" },
        // { value: "anchor_on_page", title: "Anchor On Page" },
        // { value: "open_submission_form", title: "Open Submission Form" },
        // { value: "call_custom_component", title: "Call Custom Component" },
        // { value: "parent_menu", title: "Parent Menu" },
        { value: "redirect", title: "Hard Redirect" },
        // { value: "none", title: "None" },
      ],
      menuStyles: [
        { value: "link", title: "Text" },
        { value: "button", title: "Button" },
      ],
      isSchemaFieldUploading: false,
      rules: {
        customUrl: (v) => {
          if (!v) return true; // Allow empty

          if (v.startsWith("/")) return "URL cannot start with /";
          if (v.endsWith("/")) return "URL cannot end with /";
          if (!/^[\w\-/]*$/.test(v))
            return "URL can only contain letters, numbers, hyphens, and slashes";
          if (/\/\//.test(v)) return "URL cannot contain consecutive slashes";

          return this.validateReservedUrl(this.getUrlPrefix() + v);
        },
      },
    };
  },
  created() {
    this.linkMode = "link" in this.$route.query;

    if (
      this.form.schema &&
      this.form.schema.use_random_tech_names &&
      !this.form.id
    ) {
      this.form.tech_name = this.generateUUID();
    }

    // Set default URL for new pages
    this.initializeUrl();

    if (this.form.page_attributes) {
      this.form.page_attributes.title = this.form.title;
    }
  },
  watch: {
    "form.title"(value) {
      if (this.form.page_attributes) {
        this.form.page_attributes.title = value;
      }
    },
  },
  methods: {
    copyLink() {
      copyToClipboard(
        `${this.$route.params.schema_id}_${this.$route.params.id}_`
      );
    },
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    updateUrl(value) {
      // Add prefix to the value when updating
      const prefix = this.getUrlPrefix();
      this.form.page_attributes.url = prefix + value;
    },
    getUrlPrefix() {
      // Get the path prefix from schema, ensuring it ends with /
      let prefix = this.form?.schema?.page_prefix || "";
      if (prefix && !prefix.endsWith("/")) {
        prefix = prefix + "/";
      }
      // Remove leading slash if present
      if (prefix.startsWith("/")) {
        prefix = prefix.slice(1);
      }
      return prefix;
    },
    initializeUrl() {
      const prefix = this.getUrlPrefix();

      // For new pages (no form.id), set to just the prefix
      if (!this.form.id && prefix) {
        if (!this.form.page_attributes) {
          this.form.page_attributes = {};
        }
        this.form.page_attributes.url = prefix;
      }
      // For existing pages, ensure URL has prefix
      else if (
        this.form.id &&
        this.form.page_attributes &&
        this.form.page_attributes.url &&
        prefix &&
        !this.form.page_attributes.url.startsWith(prefix)
      ) {
        this.form.page_attributes.url = prefix + this.form.page_attributes.url;
      }
    },
    updateUploadingStatus(status) {
      this.isSchemaFieldUploading = status;
    },
    validateReservedUrl(v) {
      if (
        this.$isProjectSlug("ideasunited.com") &&
        (v || "").toString().toLowerCase().startsWith("watch/")
      ) {
        return "URLs starting with \"watch/\" are reserved for the Videos page. Please choose a different URL.";
      }
      return true;
    },
  },
  computed: {
    urlWithoutPrefix() {
      // Display URL without prefix in the text field
      const prefix = this.getUrlPrefix();

      if (
        this.form.page_attributes &&
        this.form.page_attributes.url &&
        prefix &&
        this.form.page_attributes.url.startsWith(prefix)
      ) {
        return this.form.page_attributes.url.slice(prefix.length);
      }
      return this.form.page_attributes?.url || "";
    },
    getCustomUrlPrefix() {
      const buildUrl = (domain) => {
        let prefix = domain || "";

        let customPrefix = this.form?.schema?.page_prefix;

        if (customPrefix) {
          if (!customPrefix.startsWith("/")) customPrefix = "/" + customPrefix;
          prefix += customPrefix;
        }

        if (!prefix.endsWith("/")) prefix = prefix + "/";

        return prefix;
      };

      return [
        buildUrl(this.currentUser.staging_domain),
        buildUrl(this.currentUser.production_domain),
      ];
    },
    pageUrlHasUnderscore() {
      return (
        this.form.page_attributes &&
        this.form.page_attributes.url &&
        this.form.page_attributes.url.includes("_")
      );
    },
  },
};
</script>

<style scoped>
.warning-message {
  color: #787878;
  font-size: 14px;
}
</style>
